import { useNavigate } from "react-router-dom";
import { EllipsisIcon, LogOutIcon, UserRoundIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";

const ROLE_LABEL = { admin: "Adminisztrátor", marketer: "Marketinges" } as const;

function initials(name: string, email: string): string {
  const source = name.trim() !== "" ? name : email;
  const parts = source.split(/[\s.@_-]+/).filter((part) => part.length > 0);
  const letters = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "");
  return letters.join("") || "?";
}

/** Who is signed in, and the way out: the last row of the sidebar. */
export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user === null) return null;
  const name = user.full_name.trim() !== "" ? user.full_name : user.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex w-full cursor-pointer items-center gap-2.5 rounded-inner px-2 py-1.5 text-left text-foreground transition-colors hover:bg-sidebar-accent"
        aria-label={`Fiók: ${name}`}
      >
        <span
          aria-hidden="true"
          className="flex size-7.5 shrink-0 items-center justify-center rounded-full bg-tertiary text-label font-semibold text-primary-foreground"
        >
          {initials(user.full_name, user.email)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-detail font-semibold">{name}</span>
          <span className="truncate text-label text-muted-foreground">{ROLE_LABEL[user.role]}</span>
        </span>
        <EllipsisIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate font-medium">{name}</span>
          <span className="truncate text-muted-foreground">{user.email}</span>
          <span className="caps-label mt-1">{ROLE_LABEL[user.role]}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/profilom")}>
          <UserRoundIcon />
          Profilom
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={logout}>
          <LogOutIcon />
          Kilépés
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
